import { Accordion, Grid } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { AccordionFamilyCoexistence as AccordionFamilyCoexistenceVM } from 'shell/view-models/accordion-family-coexistence';
import TableFamily from './table-family';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { FamilyVM } from 'shell/view-models/family';
import { DateTime } from 'luxon';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { AccordionDetails, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { CommonInterfaceSubscription } from 'subscription/view-models/common-interface-subscription';
import { FormikProps } from 'formik';

interface FamilyCoexistenceProps {
  values?: AccordionFamilyCoexistenceVM;
  formContext: FormikProps<CommonInterfaceSubscription>;
}

const FamilyCoexistenceListComponent = ({
  values,
  formContext,
}: FamilyCoexistenceProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const [familyMembers] = useHttpFetch<HttpResponse<ResponseAnprData>, Array<FamilyVM>>(
    `${__PERSON_NEW_API_URL__}/persona/ricerca-famiglia`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            // Fixed value for request family
            tipoScheda: '1',
            identificativoANPR: values?.identifierAnpr || undefined,
          },
          datiRichiesta: {
            schedaAnagraficaRichiesta: '1',
            ricercaNazionale: '0',
            // Fixed value for request family
            datiAnagraficiRichiesti: ['02', '10', '21', '22'],
          },
        },
      },
      transformer: (response) => {
        return response.data.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto
          ?.sort((a, b) => {
            const codiceLegameA = a?.legameSoggetto?.codiceLegame
              ? Number.parseInt(a?.legameSoggetto?.codiceLegame, 10)
              : undefined;
            const codiceLegameB = b?.legameSoggetto?.codiceLegame
              ? Number.parseInt(b?.legameSoggetto?.codiceLegame, 10)
              : undefined;

            const birthDateA = a?.generalita?.dataNascita
              ? DateTime.fromISO(a?.generalita?.dataNascita)
              : null;
            const birthDateB = b?.generalita?.dataNascita
              ? DateTime.fromISO(b?.generalita?.dataNascita)
              : null;

            if (codiceLegameA && codiceLegameB && codiceLegameA < codiceLegameB) {
              return -1;
            }
            if (codiceLegameA && codiceLegameB && codiceLegameA > codiceLegameB) {
              return 1;
            }

            if (
              codiceLegameA &&
              codiceLegameB &&
              codiceLegameA === codiceLegameB &&
              birthDateA &&
              birthDateB &&
              birthDateA < birthDateB
            ) {
              return -1;
            }
            if (
              codiceLegameA &&
              codiceLegameB &&
              codiceLegameA === codiceLegameB &&
              birthDateA &&
              birthDateB &&
              birthDateA > birthDateB
            ) {
              return 1;
            }
            return 0;
          })
          ?.map((subject) => {
            const generality = transformGenerality(subject);
            const person = transformPerson(subject);
            let birthPlace = '';
            // birthplace check for object
            if (subject.generalita?.luogoNascita?.localita) {
              birthPlace = `${
                subject.generalita?.luogoNascita?.localita.descrizioneLocalita || ''
              } (${subject.generalita?.luogoNascita?.localita.descrizioneStato || ''})`;
            }

            if (subject.generalita?.luogoNascita?.comune) {
              birthPlace = `${subject.generalita?.luogoNascita?.comune.nomeComune || ''} (${
                subject.generalita?.luogoNascita?.comune.siglaProvinciaIstat || ''
              })`;
            }

            return {
              surname: subject.generalita?.cognome || '',
              name: subject.generalita?.nome || '',
              fiscalCode: subject.generalita?.codiceFiscale?.codFiscale || '',
              birthDate: subject.generalita?.dataNascita
                ? DateTime.fromISO(subject.generalita?.dataNascita).toFormat('dd/MM/yyyy')
                : '',
              relationship: subject.legameSoggetto?.descrizioneCodiceLegame || '',
              gender: subject.generalita?.sesso || '',
              aire: subject.generalita?.soggettoAIRE || '',
              birthPlace,
              generality,
              person,
              personInformation: {
                maritalStatus: subject?.statoCivile?.statoCivile || '',
                withoutMaritalStatus: subject?.statoCivile?.statoCivileND === '1' ? true : false,
                maritalStatusNote: subject?.statoCivile?.noteStatoCivile || '',
                qualification: subject.altriDati?.titoloStudio || '',
                jobPosition: subject.altriDati?.posizioneProfessionale || '',
                conditionNotProfessional: subject.altriDati?.condizioneNonProfessionale || '',
              },
            };
          });
      },
    }
  );

  useEffect(() => {
    const castedMembers = (familyMembers.data || []) as Array<FamilyVM>;

    if (castedMembers.length === 0) {
      return;
    }

    // se padre e madre non hanno il CF va cercato tra i membri della famiglia

    let newValues: CommonInterfaceSubscription = {
      ...(formContext.values || {}),
    };

    if (!formContext.values.fatherhood?.generality?.fiscalCode) {
      const newFiscalCode = castedMembers.find(
        (item) =>
          item.name === formContext.values.fatherhood?.generality?.name &&
          item.surname === formContext.values.fatherhood?.generality?.surname
      )?.fiscalCode;

      newValues = {
        ...newValues,
        fatherhood: {
          generality: {
            ...(newValues.fatherhood?.generality || {}),
            fiscalCode: newFiscalCode,
          },
        },
      };
    }

    if (!formContext.values.motherhood?.generality?.fiscalCode) {
      const newFiscalCode = castedMembers.find(
        (item) =>
          item.name === formContext.values.motherhood?.generality?.name &&
          item.surname === formContext.values.motherhood?.generality?.surname
      )?.fiscalCode;

      newValues = {
        ...newValues,
        motherhood: {
          generality: {
            ...(newValues.motherhood?.generality || {}),
            fiscalCode: newFiscalCode,
          },
        },
      };
    }

    formContext.setValues(newValues);
  }, [familyMembers.data]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="filter"
        id="filter"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>Componenti della famiglia</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {familyMembers.data ? (
              <TableFamily data={(familyMembers.data as Array<FamilyVM>) || []} />
            ) : undefined}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

FamilyCoexistenceListComponent.displayName = 'Family Coexistence list component';

const FamilyCoexistenceList = memo(FamilyCoexistenceListComponent);

export default FamilyCoexistenceList;
